// Right-side inspector for the selected flow node. Shows the node's route,
// asserts, inputs, transitions, and fingerprint signals — the four schema
// categories per PRD §5.

import { Button } from '../ui/button.jsx'

function Section({ title, children }) {
  return (
    <section className="border-t border-[var(--color-sandstone)] px-5 py-4">
      <h3 className="mb-2 text-xs font-medium uppercase tracking-wider text-[var(--color-schist)]">
        {title}
      </h3>
      {children}
    </section>
  )
}

function EmptyHint({ children }) {
  return <p className="text-xs text-[var(--color-schist)]">{children}</p>
}

export function FlowSidePanel({ node, onClose }) {
  if (!node) return null
  const {
    label,
    route,
    asserts = [],
    inputs = [],
    transitions = [],
    fingerprint,
    isEntry,
  } = node.data

  return (
    <aside
      aria-label="Node inspector"
      className="flex h-full w-[360px] flex-col border-l border-[var(--color-sandstone)] bg-[var(--color-limestone)]"
    >
      <header className="flex items-start justify-between gap-3 px-5 py-4">
        <div>
          <div className="text-[10px] font-medium uppercase tracking-wider text-[var(--color-schist)]">
            {isEntry ? 'Entry node' : 'Screen'}
          </div>
          <h2 className="text-lg font-semibold text-[var(--color-basalt)]">{label}</h2>
          {route && (
            <code className="mt-1 inline-block font-mono text-xs text-[var(--color-slate)]">
              {route}
            </code>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          aria-label="Close inspector"
          className="!h-7 !w-7 !p-0"
        >
          ×
        </Button>
      </header>

      <div className="flex-1 overflow-y-auto">
        <Section title="Asserts">
          {asserts.length === 0 ? (
            <EmptyHint>No asserts declared.</EmptyHint>
          ) : (
            <ul className="space-y-1.5">
              {asserts.map((a, i) => (
                <li
                  // biome-ignore lint/suspicious/noArrayIndexKey: asserts is a short static array per render
                  key={`assert-${i}`}
                  className="rounded-md bg-[var(--color-sandstone)]/50 px-3 py-2 text-xs text-[var(--color-slate)]"
                >
                  {Object.entries(a).map(([k, v]) => (
                    <div key={k}>
                      <span className="font-mono text-[var(--color-schist)]">{k}:</span>{' '}
                      <span className="font-mono text-[var(--color-basalt)]">
                        {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                      </span>
                    </div>
                  ))}
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title="Inputs">
          {inputs.length === 0 ? (
            <EmptyHint>No inputs declared.</EmptyHint>
          ) : (
            <ul className="space-y-1.5">
              {inputs.map((inp, i) => (
                <li
                  // biome-ignore lint/suspicious/noArrayIndexKey: inputs is a short static array per render
                  key={`input-${i}`}
                  className="flex items-center justify-between rounded-md bg-[var(--color-sandstone)]/50 px-3 py-2 text-xs"
                >
                  <span className="font-mono text-[var(--color-basalt)]">
                    {inp.field ?? inp.name ?? `input ${i}`}
                  </span>
                  {inp.fixture && (
                    <span className="font-mono text-[10px] text-[var(--color-terracotta)]">
                      {inp.fixture}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title="Transitions">
          {transitions.length === 0 ? (
            <EmptyHint>Terminal node — no outbound transitions.</EmptyHint>
          ) : (
            <ul className="space-y-1.5">
              {transitions.map((t, i) => (
                <li
                  // biome-ignore lint/suspicious/noArrayIndexKey: transitions is a short static array per render
                  key={`transition-${i}`}
                  className="flex items-center justify-between rounded-md bg-[var(--color-sandstone)]/50 px-3 py-2 text-xs"
                >
                  <span className="font-mono text-[var(--color-schist)]">{t.on ?? '→'}</span>
                  <span className="font-mono text-[var(--color-basalt)]">{t.to ?? t.target}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title="Fingerprint">
          {!fingerprint ? (
            <EmptyHint>No fingerprint declared. Drift detection uses defaults.</EmptyHint>
          ) : (
            <dl className="space-y-1 text-xs">
              {Object.entries(fingerprint).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-3">
                  <dt className="font-mono text-[var(--color-schist)]">{k}</dt>
                  <dd className="truncate font-mono text-[var(--color-basalt)]">
                    {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </Section>
      </div>
    </aside>
  )
}
